<template>
  <div>
    <template v-if="isLoading"> ... </template>
    <div v-else class="flex-1 flex flex-col items-center gap-y-8">
      <div class="flex-1 w-full xl:w-4/6 flex justify-center flex-col md:flex-row gap-8">
        <form action="" class="w-full flex flex-col gap-y-4" @submit.prevent="onSubmit">
          <div class="w-full flex flex-col md:flex-row gap-2">
            <TextInput
              :show-error-spacer="false"
              :label="$t('EnFirstName')"
              :rules="[requiredRule, englishRule, maxRule(50)]"
              direction="flex-col justify-start"
              label-class="w-full"
              field-name="englishFirstName"
              is-required
              class="flex-1"
            ></TextInput>
            <TextInput
              :show-error-spacer="false"
              :label="$t('EnFamilyName')"
              :rules="[requiredRule, englishRule, maxRule(50)]"
              direction="flex-col justify-start"
              label-class="w-full"
              field-name="englishFamilyName"
              is-required
              class="flex-1"
            ></TextInput>
          </div>
          <div class="w-full flex flex-col md:flex-row gap-2">
            <TextInput
              :show-error-spacer="false"
              :label="$t('ArFamilyName')"
              :rules="[requiredRule, arabicRule, maxRule(50)]"
              direction="flex-col justify-start"
              label-class="w-full"
              field-name="arabicFirstName"
              is-required
              class="flex-1"
            ></TextInput>
            <TextInput
              :show-error-spacer="false"
              :label="$t('ArFirstName')"
              :rules="[requiredRule, arabicRule, maxRule(50)]"
              direction="flex-col justify-start"
              label-class="w-full"
              field-name="arabicFamilyName"
              is-required
              class="flex-1"
            ></TextInput>
          </div>
          <div class="w-full flex flex-col md:flex-row gap-2">
            <DropdownSearch
              :show-error-spacer="false"
              :label="$t('Gender')"
              :rules="[requiredRule]"
              :options="lookupsStore.genders"
              direction="flex-col justify-start"
              label-class="w-full"
              field-name="gender"
              is-required
              class="flex-1"
            ></DropdownSearch>
            <ComboText
              :show-error-spacer="false"
              :label="$t('MobileNo')"
              :rules="[requiredRule, mobileRule]"
              :options="lookupsStore.countriesAsPhoneCodes"
              :fuzzyKeywords="[
                'diallingCode',
                'localizedName',
                'isoThreeCharactersCode',
                'isoTwoCharactersCode'
              ]"
              defaultValue="00971"
              direction="flex-col justify-start"
              label-class="w-full"
              field-name="mobileNumber"
              is-required
              class="flex-1"
            ></ComboText>
          </div>
          <div class="w-full flex flex-col md:flex-row gap-2">
            <TextInput
              :show-error-spacer="false"
              :label="$t('IdentityNumber')"
              :rules="[requiredRule]"
              :default-value="props.data.eid"
              direction="flex-col justify-start"
              label-class="w-full"
              is-read-only
              field-name="eid"
              is-required
              placeholder="###-####-#######-#"
              mask="###-####-#######-#"
              class="flex-1"
            ></TextInput>
            <DatepickerCalendar
              :show-error-spacer="false"
              :label="$t('DateOfBirth')"
              :rules="[requiredRule]"
              :default-value="new Date(props.data.dateOfBirth)"
              label-class="w-full"
              direction="flex-col justify-start"
              field-name="dateOfBirth"
              is-required
              :max-date="nowDate"
              class="flex-1"
            ></DatepickerCalendar>
          </div>
          <div class="w-full flex flex-col md:flex-row gap-2">
            <TextInput
              :show-error-spacer="false"
              :label="$t('Email')"
              :rules="[requiredRule, validateUserNameRule]"
              direction="flex-col justify-start"
              label-class="w-full"
              field-name="username"
              is-required
              class="flex-1"
              :debounce-time="500"
            ></TextInput>
            <div class="flex-1"></div>
          </div>
          <div class="w-full flex flex-col md:flex-row gap-2">
            <TextInput
              :show-error-spacer="false"
              :label="$t('Password')"
              :rules="[requiredRule, passwordRule]"
              input-type="password"
              direction="flex-col justify-start"
              label-class="w-full"
              field-name="password"
              is-required
              class="flex-1"
              :hint="$t('loginPasswordValidation')"
              show-password
              show-password-bar
            ></TextInput>
            <TextInput
              :show-error-spacer="false"
              :label="$t('confirmPassword')"
              :rules="[requiredRule, sameValueRule]"
              :dependentOn="form.values.password"
              input-type="password"
              direction="flex-col justify-start"
              label-class="w-full"
              field-name="confirmPassword"
              is-required
              class="flex-1"
              show-password
            ></TextInput>
          </div>
        </form>
      </div>
      <div class="w-full">
        <FormFooter @onBack="$emit('onBack')" @onNext="submitForm"></FormFooter>
      </div>
    </div>
  </div>
</template>

<script setup>
import * as Yup from 'yup'
import ComboText from '@/components/Form/ComboText.vue'
import DropdownSearch from '@/components/Form/DropdownSearch.vue'
import InfoBar from '@/components/Form/InfoBar.vue'
import TextInput from '@/components/Form/TextInput.vue'
import TwowayDropdown from '@/components/Form/TwowayDropdown.vue'
import DatepickerCalendar from '@/components/Form/DatepickerCalendar.vue'
import ReactiveDropdown from '@/components/Form/ReactiveDropdown.vue'
import RecaptchaTicker from '@/components/Form/RecaptchaTicker.vue'
import FormFooter from '@/components/Onboarding/FormFooter.vue'

import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { notify } from 'notiwind'
import { useLookupsStore } from '@/stores/lookups'
import { useForm } from 'vee-validate'
import { useFormStore } from '@/stores/form'
import registerApi from '@/services/register'
import router from '@/router'
import { DateTime } from 'luxon'

const props = defineProps({
  data: Object
})
const i18n = useI18n()
const route = useRoute()
const isLoading = ref(true)
const lookupsStore = useLookupsStore()
const formStore = useFormStore()
const requiredRule = ref({
  name: 'required',
  message: 'INVALID_REQUIRED'
})
const emailRule = ref({
  name: 'email',
  message: 'INVALID_EMAIL'
})
const maxRule = (count) => {
  return {
    name: 'max_char',
    value: count,
    message: 'INVALID_MAX_CHAR'
  }
}
const englishRule = ref({
  name: 'englishText',
  message: 'EnglishContentOnly'
})
const arabicRule = ref({
  name: 'arabicText',
  message: 'ArabicContentOnly'
})
const mobileRule = ref({
  name: 'mobile',
  message: 'inValidLocalMobile'
})
const validateUserNameRule = ref({
  name: 'validateUserName',
  message: 'INVALID_EMAIL'
})
const passwordRule = ref({
  name: 'password',
  message: 'PasswordPattern'
})
const sameValueRule = ref({
  name: 'sameValue',
  value: 'password',
  message: 'PasswordNotEqual'
})

const nowDate = ref()
nowDate.value = new Date()
const recaptchaResponse = ref()

const form = useForm()
formStore.$reset()
formStore.form = form

const submitForm = async () => {
  try {
    form.setTouched(true)
    const validationResult = await form.validate()
    if (!validationResult.valid) return
  } catch (error) {
    console.error('submit form error', error)
  }
  if (Object.keys(form.errors.value)?.length === 0) {
    const payload = {
      residenceCountryId: props.data.residenceCountryId,
      verficationType: props.data.verficationType,
      OneTimePassword: props.data.otp,
      registerdMobileNumberId: props.data.registerdMobileNumberId,
      recaptchaResponse: recaptchaResponse.value,
      email: form.values.username,
      identityNumber: form.values.eid.split('-').join(''),
      mobileNumber: form.values.mobileNumber,
      englishFirstName: form.values.englishFirstName,
      arabicFirstName: form.values.arabicFirstName,
      englishFamilyName: form.values.englishFamilyName,
      arabicFamilyName: form.values.arabicFamilyName,
      gender: form.values.gender,
      password: form.values.password,
      dateOfBirth: DateTime.fromMillis(form.values.dateOfBirth).toFormat('dd/MM/yyyy')
    }
    try {
      await registerApi.registerResident(payload, route.query.token)
      notify(
        {
          title: 'success',
          text: i18n.t('RegistrationSuccess')
        },
        3000
      )
      router.replace('/')
    } catch (error) {
      console.error('register from error', error)
    }
  }
}

onMounted(async () => {
  await lookupsStore.getGenders()
  await lookupsStore.getCountries()
  isLoading.value = false
})
onBeforeUnmount(() => {
  formStore.$reset()
})
</script>

<style lang="scss" scoped></style>
